import React, { useMemo } from 'react';
import { Modal, notification, Form, Select, Button } from 'antd';
import { gql } from 'apollo-boost';
import {useMutation, useQuery} from '@apollo/react-hooks';
import moment from 'moment';
import LoaderOverlay from 'shared/components/LoaderOverlay';

interface EditDayTourModalProps {
    deliveryId: string;
    day: string; // attendu format 'YYYY-MM-DD'
    deliveryName: string;
    deliveryStatus: string;
    visible: boolean;
    onClose: () => void;
}

const GET_DATA = gql`
query getData($date: Date!, $clientType: String!) {
    deliveries(date: $date, clientType: $clientType) {
        id
        status
    }
}
`;

const MODIFY_DAY_TOUR = gql`
    mutation modifyDayTour($input: MealInput!) {
        modifyDayTour(input: $input) {
            id
        }
    }
`;

interface Data {
    deliveries: Delivery[];
}

export default function EditDayTourModal({
                                             visible,
                                             deliveryId,
                                             day,
                                             deliveryName,
                                             deliveryStatus,
                                             onClose,
                                             deliveryStarted
                                         }: EditDayTourModalProps) {
    const handleMutationError = (error: any) => {
        notification.error({
            message: "Échec de la modification",
            description: error?.message || "Une erreur est survenue.",
        });
    };

    // Calcule J-1 et J-2 par rapport à `day`
    const d1 = moment(day, 'YYYY-MM-DD').clone().subtract(1, 'day').format('YYYY-MM-DD');
    const d2 = moment(day, 'YYYY-MM-DD').clone().subtract(2, 'day').format('YYYY-MM-DD');

// Query pour récupérer les livraisons du jour J-1
    const { data: dataD1, loading: d1Loading } = useQuery<Data>(GET_DATA, {
        variables: { date: d1, clientType: 'senior' },
        fetchPolicy: 'cache-and-network',
    });

// Query pour récupérer les livraisons du jour J-2
    const { data: dataD2, loading: d2Loading } = useQuery<Data>(GET_DATA, {
        variables: { date: d2, clientType: 'senior' },
        fetchPolicy: 'cache-and-network',
    });

    const options = useMemo(() => {
        const opts: { value: string; label: string }[] = [];

        const isSunday = (date: string) => moment(date, "YYYY-MM-DD").day() === 0; // 0 = dimanche

        const deliveryStartedD1 = !!dataD1?.deliveries.find(
            (d) => d.status !== "pending"
        );
        const deliveryStartedD2 = !!dataD2?.deliveries.find(
            (d) => d.status !== "pending"
        );

        // D1
        if (!deliveryStartedD1 && !isSunday(d1)) {
            opts.push({
                value: d1,
                label: `${moment(d1, "YYYY-MM-DD").format("DD/MM/YYYY")} (J-1)`,
            });
        }

        // D2
        if (!deliveryStartedD2 && !isSunday(d2)) {
            opts.push({
                value: d2,
                label: `${moment(d2, "YYYY-MM-DD").format("DD/MM/YYYY")} (J-2)`,
            });
        } else if (isSunday(d2)) {
            // si d2 est dimanche → proposer J-3 à la place
            const d3 = moment(d2, "YYYY-MM-DD").subtract(1, "day").format("YYYY-MM-DD");
            opts.push({
                value: d3,
                label: `${moment(d3, "YYYY-MM-DD").format("DD/MM/YYYY")} (J-3)`,
            });
        }

        return opts;
    }, [d1, d2, dataD1, dataD2]);


    const [form] = Form.useForm();

    const [modifyDayTour, { loading }] = useMutation(MODIFY_DAY_TOUR, {
        // Les tournées sont régénérées : on recharge la journée affichée
        refetchQueries: ['getData'],
        awaitRefetchQueries: true,
        onCompleted: () => {
            notification.success({
                message:
                    "Le jour de livraison de la tournée a été modifié et les tournées ont été régénérées",
            });
            onClose();
        },
        onError: handleMutationError,
    });

    const onFinish = async (values: { newDay: string }) => {
        await modifyDayTour({
            variables: {
                input: {
                    deliveryId,
                    day: values.newDay,
                    oldDay: day
                },
            },
        });
    };


    if ((!dataD1 && d1Loading) || (!dataD2 && d2Loading)) {
        return (
            <div className="__loading">
                <LoaderOverlay loading />
            </div>
        );
    }

    return (
        <Modal
            className="edit-tour-modal"
            title={`Modifier le jour de livraison de la tournée ${deliveryName}`}
            visible={visible}
            onCancel={onClose}
            footer={null}
            destroyOnClose
        >
            {options.length === 0 && (
                <p>Aucune date disponible pour décaler la tournée.</p>
            )}

            {!deliveryStarted && options.length > 0 && (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{ newDay: options[0]?.value }}
                >
                    <Form.Item
                        label="Sélectionner un nouveau jour de livraison"
                        name="newDay"
                        rules={[{ required: true, message: "Choisis un jour" }]}
                    >
                        <Select
                            options={options}
                            placeholder="Choisir J-1 ou J-2"
                            disabled={options.length === 0}
                        />
                    </Form.Item>

                    <div className="flex gap-2 justify-end">
                        <Button onClick={onClose}>Annuler</Button>
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Enregistrer
                        </Button>
                    </div>
                </Form>
            )}
        </Modal>
    );


}
